// react
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

// components
import { Icon } from '@/components/atoms';
import { Chat, Launcher } from '@/components/organisms';

// models
import { ChatMessage } from '@karehero/models';

// assets
// @transform-path ../../../../../../../src/assets/loader/keni.svg
import keni from '@/assets/loader/keni.svg';

interface ChatProps {
  className?: string;
  messages?: ChatMessage[];
  onSend?: (message: string) => Promise<void>;
  onReset?: () => Promise<void>;
  isTyping?: boolean;
  isMobile?: boolean;
  offsetY?: number;
}

/**
 * Chat a chat widget.
 */
export const ChatFAB = ({
  className,
  messages,
  onSend,
  onReset,
  isTyping,
  isMobile,
  offsetY,
}: ChatProps) => {
  // state
  const [isOpen, setIsOpen] = useState(false);

  // refs
  const ref = useRef<HTMLDivElement>(null);

  // effects
  useEffect(() => {
    ref.current?.scrollIntoView({});
  }, [messages, isTyping, isOpen]);

  return (
    <Wrapper className={className} $isMobile={isMobile} $offsetY={offsetY}>
      {isOpen ? (
        <ChatBody>
          <Header>
            <HeaderItem>
              <Keni>
                <img src={keni} alt='Keni' />
              </Keni>
              <Name>KareHero AI</Name>
            </HeaderItem>
            <HeaderItem>
              <CloseButton onClick={() => setIsOpen(false)}>
                <Icon icon='xmark' fontSize={24} />
              </CloseButton>
            </HeaderItem>
          </Header>
          <Chat messages={messages} isTyping={isTyping} onSend={onSend} />
        </ChatBody>
      ) : (
        <Launcher isVisible isOpen={isOpen} setIsOpen={setIsOpen} />
      )}
    </Wrapper>
  );
};

interface WrapperProps {
  $isMobile?: boolean;
  $offsetY?: number;
}
const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  bottom: 0px;
  right: 0px;
  ${(props) =>
    props.$isMobile &&
    css`
      bottom: 70px;
    `}
  z-index: 100000;

  padding: ${(props) => props.theme.spacing[12]};
`;

const ChatBody = styled.div`
  display: inline-flex;
  flex-direction: column;

  overflow: hidden;

  background: ${(props) => props.theme.color.neutral[10]};
  border-radius: 12px;
  box-shadow: ${(props) => props.theme.elevation[2]};
  font: ${(props) => props.theme.typography.body.sm.regular};
  width: 100%;
  max-width: 400px;
  height: 600px;
  max-height: 600px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(props) => props.theme.color.primary[60]};
  gap: ${(props) => props.theme.spacing[12]};
  color: ${(props) => props.theme.color.neutral[10]};
  padding: ${(props) => props.theme.spacing[8]};
  padding-right: ${(props) => props.theme.spacing[16]};
`;

const HeaderItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[12]};
`;

const Keni = styled.div`
  width: 42px;
  height: 42px;

  & img {
    width: 100%;
    height: 100%;
  }
`;

const Name = styled.div`
  font: ${(props) => props.theme.typography.body.md.medium};
`;

const CloseButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[10]};

  transition-duration: 200ms;
  &:hover {
    color: ${(props) => props.theme.color.neutral[20]};
  }
`;

ChatFAB.displayName = 'ChatFAB';

export default ChatFAB;
